import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Container, useTheme, Pagination, IconButton, Tooltip, Breadcrumbs, Link, Typography } from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import ReactGA from 'react-ga4';
import { tracker } from '../../analytics';
import _ from 'lodash';
import TutorialModal from '../../components/TutorialModal';


// Import components
import DashboardHeader from './components/DashboardHeader';
import SearchFilterBar from './components/SearchFilterBar';
import ChartsOverview from './components/ChartsOverview';
import ContractTabs from './components/ContractTabs';
import ContractGrid from './components/ContractGrid';
import ContractList from './components/ContractList';
import ContractTable from './components/ContractTable';
import ContractKanban from './components/ContractKanban';
import RepositorySidebar from './components/RepositorySidebar';
import LoadingState from '../../components/LoadingState';
import EmptyState from './components/EmptyState';
import ContractFilterDrawer from './components/ContractFilterDrawer';
import RegulatoryAlertsList from './components/RegulatoryAlertsList';
import LifecycleList from './components/LifecycleList';

// Import dialogs
import ContractDetailDialog from './dialogs/ContractDetailDialog';
import DownloadDialog from './dialogs/DownloadDialog';
import ShareContractModal from './components/ShareContractModal';
import DeleteConfirmationDialog from './dialogs/DeleteConfirmationDialog';
import MoveContractDialog from './dialogs/MoveContractDialog';

// Import actions
import {
    fetchContracts,
    fetchContractsByTab,
    fetchTabTotalCount,
    setCurrentTab,
    setTabPage,
    deleteContracts,
    searchContracts,
    shareContract,
    resetContract,
    handleUpdateContract,
    setActiveWorkspace,
    setActiveFolder,
    setActiveSmartViewAction,
    reorderKanbanContracts,
} from '../../redux/actions/contractAction';
import { filterContracts } from '../../redux/actions/contractAction';
import { fetchMyPendingSignatures, fetchMyPendingSignaturesCount } from '../../redux/actions/signatureAction';
import { setOpenNavBar } from '../../redux/actions/navbarAction';




// Import API client
import { backendClient } from '../../api/backend';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { getStatusColor } from '../../utils/utils';

// Tab identifier mapping
const TAB_IDENTIFIERS = {
    0: 'all',
    1: 'recent',
    2: 'shared',
    3: 'drafts',
    4: 'in_review',
    5: 'pending_signature',
    6: 'to_sign',
    7: 'signed'
};

// Reverse mapping: identifier string → tab index
const TAB_INDEX_BY_ID = Object.fromEntries(
    Object.entries(TAB_IDENTIFIERS).map(([idx, id]) => [id, Number(idx)])
);

const ContractHistory = () => {
    // Redux state
    const dispatch = useDispatch();
    const user = useSelector(state => state.auth?.user?.user);
    const loading = useSelector(state => state.contract?.loading || false);
    const contracts = useSelector(state => state.contract?.contracts || []);
    const tabPagination = useSelector(state => state.contract?.tabPagination || {});
    const currentTabRedux = useSelector(state => state.contract?.currentTab || 0);

    // Router
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const theme = useTheme();

    // Translation
    const { t, i18n } = useTranslation();

    // Resolve initial tab from URL ?tab= param
    const initialTab = () => {
        const tabParam = searchParams.get('tab');
        if (tabParam && TAB_INDEX_BY_ID[tabParam] !== undefined) {
            return TAB_INDEX_BY_ID[tabParam];
        }
        return 0;
    };

    // Local state
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState([]);
    const [selectedContract, setSelectedContract] = useState(null);
    const [currentContractId, setCurrentContractId] = useState(null);
    const [activeView, setActiveView] = useState('kanban');
    const [activeTab, setActiveTab] = useState(initialTab);
    // Page state now managed in Redux via tabPagination
    const currentPage = tabPagination[activeTab]?.page || 1;
    const currentTotal = tabPagination[activeTab]?.totalContracts || 0;
    const rowsPerPage = 8; // Increased for better visibility

    // Hide navbar when entering contract screen
    useEffect(() => {
        dispatch(setOpenNavBar(false));
    }, [dispatch]);

    // Sidebar & preview panel state
    const [sidebarOpen, setSidebarOpen] = useState(true);
    // Active workspace/folder/smart view from Redux
    const activeWorkspace = useSelector(state => state.contract?.activeWorkspace);
    const activeFolderRedux = useSelector(state => state.contract?.activeFolder);
    const activeSmartViewRedux = useSelector(state => state.contract?.activeSmartView);
    const activeSmartViewKey = useSelector(state => state.contract?.activeSmartViewKey);
    const activeMonitoringView = useSelector(state => state.contract?.activeMonitoringView);
    const workspaces = useSelector(state => state.contract?.workspaces || []);
    const folders = useSelector(state => state.contract?.folders || []);

    // Filter state
    const [activeFilter, setActiveFilter] = useState('none');
    const [sortOrder, setSortOrder] = useState('none');
    const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
    const [activeAdvancedFilters, setActiveAdvancedFilters] = useState(0);
    const [activePartyId, setActivePartyId] = useState(null);
    const filteredContracts = useSelector(state => state.contract?.filteredContracts || []);
    const loadingFilter = useSelector(state => state.contract?.loadingFilter || false);

    // "Documents à signer" tab state from signature reducer
    const myPendingContracts = useSelector(state => state.signature?.myPendingContracts || []);
    const myPendingLoading = useSelector(state => state.signature?.myPendingLoading || false);
    const myPendingCount = useSelector(state => state.signature?.myPendingCount || 0);


    // Sidebar filter context ref — tracks current sidebar filter params for composing search/filters
    const sidebarFilterParamsRef = useRef(null);

    // Dialog flow state
    const [dialogSource, setDialogSource] = useState(null);

    // Chart data
    const [statusData, setStatusData] = useState([]);
    const [typeData, setTypeData] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    // Dialog states
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [contractVideoModalOpen, setContractVideoModalOpen] = useState(false);
    // Move-to dialog state
    const [moveDialogState, setMoveDialogState] = useState({
        open: false,
        contractIds: [],
        currentWorkspaceId: null,
        currentFolderId: null,
    });

    // Initialize Google Analytics
    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: '/contract-dashboard',
            title: 'Contract Dashboard'
        });
    }, []);

    // Note: Total count is now fetched per tab via fetchTabTotalCount in the useEffect
    // The old fetchTotalCount function has been removed

    // Fetch contracts when user, tab, page, or view changes
    useEffect(() => {
        if (user && user.id) {
            const tabIdentifier = TAB_IDENTIFIERS[activeTab];
            const page = tabPagination[activeTab]?.page || 1;
            const offset = (page - 1) * rowsPerPage;

            if (tabIdentifier === 'to_sign') {
                // Use signature-specific endpoint for "Documents à signer" tab
                dispatch(fetchMyPendingSignatures(rowsPerPage, offset));
                dispatch(fetchMyPendingSignaturesCount());
            } else if (activeView === 'kanban') {
                // Kanban needs all contracts — no pagination
                dispatch(fetchContractsByTab(tabIdentifier, 1000, 0, true));
            } else {
                // Fetch contracts for current tab with pagination
                dispatch(fetchContractsByTab(tabIdentifier, rowsPerPage, offset, true));
                // Fetch total count for current tab
                dispatch(fetchTabTotalCount(tabIdentifier));
            }
        }
    }, [user, dispatch, activeTab, activeView, tabPagination[activeTab]?.page, rowsPerPage]);

    // When a smart view is already active on mount (e.g. navigated from dashboard),
    // fire the filter API call so filteredContracts gets populated.
    useEffect(() => {
        if (activeSmartViewRedux && activeSmartViewKey) {
            dispatch(filterContracts({ smartView: activeSmartViewKey }, rowsPerPage, 0));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Process contract data for charts
    useEffect(() => {
        if (contracts && contracts.length > 0) {
            // Process status data
            const statuses = _.groupBy(contracts, 'status');
            const statusStats = Object.keys(statuses).map(status => ({
                name: status,
                value: statuses[status].length,
                color: getStatusColor(status, theme)
            }));
            setStatusData(statusStats);

            // Process type data
            const types = _.groupBy(contracts, 'type');
            const typeStats = Object.keys(types).map((type, index) => ({
                name: type || 'Unspecified',
                value: types[type].length,
                color: [
                    theme.palette.primary.main,
                    theme.palette.success.main,
                    theme.palette.warning.main,
                    theme.palette.secondary.main,
                    theme.palette.error.light
                ][index % 5]
            }));
            setTypeData(typeStats);

            // Process monthly data
            const monthlyStats = processMonthlyData(contracts);
            setMonthlyData(monthlyStats);
        }
    }, [contracts]);

    const handleAdvancedFilter = (filteredResults) => {
        // If we have filtered results, use them, otherwise show all contracts
        if (filteredResults && filteredResults.length > 0) {
            // Count the number of active filters (this could be more sophisticated based on your needs)
            setActiveAdvancedFilters(1);
        } else {
            setActiveAdvancedFilters(0);
        }
    };

    const handleOpenAdvancedFilter = () => {
        setIsAdvancedFilterOpen(true);
    };

    const handleCloseAdvancedFilter = () => {
        setIsAdvancedFilterOpen(false);
    };

    // Process monthly data for the line chart
    const processMonthlyData = (contracts) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyData = months.map(month => ({
            name: month,
            Created: 0,
            Updated: 0,
            Signed: 0
        }));

        contracts.forEach(contract => {
            if (!contract.created_at || !contract.updated_at) return;

            const createdDate = new Date(contract.created_at);
            const updatedDate = new Date(contract.updated_at);
            const createdMonth = createdDate.getMonth();
            const updatedMonth = updatedDate.getMonth();

            monthlyData[createdMonth].Created += 1;
            monthlyData[updatedMonth].Updated += 1;

            if (contract.status === 'SIGNED') {
                monthlyData[updatedMonth].Signed += 1;
            }
        });

        return monthlyData;
    };

    // Debounced search function — composes with active sidebar context
    const debouncedSearch = useCallback(
        debounce(term => {
            if (!user || !user.id) return;

            // If monitoring view is active, search is handled locally in the component
            if (activeMonitoringView) return;

            // If sidebar context is active (workspace/folder/smart view), compose search with it
            if (sidebarFilterParamsRef.current) {
                if (term.trim()) {
                    dispatch(filterContracts({ ...sidebarFilterParamsRef.current, searchTerm: term }, rowsPerPage, 0));
                } else {
                    // Search cleared — restore sidebar context
                    dispatch(filterContracts(sidebarFilterParamsRef.current, rowsPerPage, 0));
                }
            } else {
                // Global search (no sidebar context)
                dispatch(searchContracts(user.id, term));
            }
        }, 500),
        [dispatch, user, activeMonitoringView, rowsPerPage]
    );

    const handleSearch = event => {
        const term = event.target.value;
        setSearchTerm(term);
        debouncedSearch(term);

        // Google Analytics event for search
        ReactGA.event({
            category: 'Search',
            action: 'User searched contracts',
            label: term
        });
        tracker.event({
            category: 'Search',
            action: 'User searched contracts',
            label: term
        });
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        // Ensure newPage is an integer
        const pageNumber = parseInt(newPage, 10);

        // Update page for current tab in Redux
        dispatch(setTabPage(activeTab, pageNumber));

        // Fetch new page if not searching/filtering
        if (searchTerm.trim() === '' && activeFilter === 'none' && activeAdvancedFilters === 0) {
            const tabIdentifier = TAB_IDENTIFIERS[activeTab];
            const offset = (pageNumber - 1) * rowsPerPage;
            dispatch(fetchContractsByTab(tabIdentifier, rowsPerPage, offset));
        }
    };

    // Dialog management functions
    const handleContractClick = (contract) => {
        setSelectedContract(contract);
        setIsDetailModalOpen(true);
        setDialogSource(null); // Reset source since this is the first dialog
    };

    const handleCloseDetailDialog = () => {
        setIsDetailModalOpen(false);
    };

    const handleDownloadContract = (contractId, source = null) => {
        setCurrentContractId(contractId);
        setIsDownloadDialogOpen(true);
        setDialogSource(source); // Track which dialog opened this one

        // Google Analytics Event
        ReactGA.event({
            category: 'Contract',
            action: 'User initiated downloading contract',
            label: `Contract ID: ${contractId}`
        });
        tracker.event({
            category: 'Contract',
            action: 'User initiated downloading contract',
            label: `Contract ID: ${contractId}`
        });
    };

    const handleCloseDownloadDialog = () => {
        setIsDownloadDialogOpen(false);

        // If opened from detail dialog, return to it
        if (dialogSource === 'detail') {
            setIsDetailModalOpen(true);
        }
    };

    const handleShareContract = (contractId, source = null) => {
        setCurrentContractId(contractId);
        setIsShareModalOpen(true);
        setDialogSource(source); // Track which dialog opened this one

        // Google Analytics Event
        ReactGA.event({
            category: 'Contract',
            action: 'User initiated sharing contract',
            label: `Contract ID: ${contractId}`
        });
        tracker.event({
            category: 'Contract',
            action: 'User initiated sharing contract',
            label: `Contract ID: ${contractId}`
        });
    };


    const handleCloseShareDialog = () => {
        setIsShareModalOpen(false);

        // If opened from detail dialog, return to it
        if (dialogSource === 'detail') {
            setIsDetailModalOpen(true);
        }
    };

    // Handle share
    // Handle share
    const handleShare = async selectedUsers => {
        if (currentContractId && selectedUsers?.length > 0) {
            try {

                await dispatch(shareContract(currentContractId, selectedUsers));
                enqueueSnackbar(t("contract_shared_success"), { variant: 'success' });

                // Refresh contracts
                // const offset = (page - 1) * rowsPerPage;
                // dispatch(fetchContracts(user.id, rowsPerPage, offset));

                // Google Analytics event for successful share
                ReactGA.event({
                    category: 'Contract',
                    action: 'Contract shared successfully',
                    label: `Contract ID: ${currentContractId}`
                });
                tracker.event({
                    category: 'Contract',
                    action: 'Contract shared successfully',
                    label: `Contract ID: ${currentContractId}`
                });

                // Don't return to detail dialog after successful share
                // This is a deliberate choice as the action is complete
                setIsShareModalOpen(false);
            } catch (error) {
                console.error('Error sharing contract:', error);
                enqueueSnackbar(t("contract_share_error"), { variant: 'error' });

                // On error, we might want to go back to the detail dialog
                if (dialogSource === 'detail') {
                    setIsDetailModalOpen(true);
                }
            }
        } else {
            console.error('No user IDs selected for sharing or invalid contract ID.');
            enqueueSnackbar(t("invalid_share_parameters"), { variant: 'error' });
        }
    };

    const handleOpenDeleteDialog = (contractIds, source = null) => {
        if (contractIds && contractIds.length > 0) {
            setSelected(Array.isArray(contractIds) ? contractIds : [contractIds]);
            setIsDeleteDialogOpen(true);
            setDialogSource(source); // Track which dialog opened this one
        } else {
            enqueueSnackbar(t('please_select_contracts'), { variant: 'warning' });
        }
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);

        // If opened from detail dialog, return to it
        if (dialogSource === 'detail') {
            setIsDetailModalOpen(true);
        }
    };

    // Delete selected contracts
    const handleDeleteContracts = async () => {
        try {
            setLoadingDelete(true);
            await dispatch(deleteContracts(selected, user.id));
            setSelected([]);

            // Reset to page 1 if deleting all contracts on the current page
            const newPage = selected.length === viewContracts.length && currentPage > 1 ? 1 : currentPage;
            if (newPage !== currentPage) {
                dispatch(setTabPage(activeTab, newPage));
            }

            // // Force refresh by passing true as the last parameter
            // const offset = (newPage - 1) * rowsPerPage;
            // await dispatch(fetchContracts(user.id, rowsPerPage, offset, true));
            // await fetchTotalCount();

            enqueueSnackbar(t("delete_contracts_success"), {
                variant: 'success'
            });

            // Google Analytics event for deleting contracts
            ReactGA.event({
                category: 'Contract',
                action: 'User deleted contracts',
                label: `Deleted Contract IDs: ${selected.join(', ')}`
            });
            tracker.event({
                category: 'Contract',
                action: 'User deleted contracts',
                label: `Deleted Contract IDs: ${selected.join(', ')}`
            });
        } catch (error) {
            enqueueSnackbar(t('delete_contracts_error'), { variant: 'error' });

            // On error, we might want to go back to the detail dialog
            if (dialogSource === 'detail') {
                setIsDetailModalOpen(true);
            }
        }
        setIsDeleteDialogOpen(false);
        setLoadingDelete(false);
    };

    useEffect(() => {

    }, [])



    // Open Move-to dialog for a single contract
    const handleMoveContract = (contract) => {
        if (!contract) return;
        setMoveDialogState({
            open: true,
            contractIds: [contract.id],
            currentWorkspaceId: contract.workspace_id || null,
            currentFolderId: contract.folder_id || null,
        });
    };

    // Open Move-to dialog for currently selected contracts (bulk)
    const handleBulkMove = () => {
        if (selected.length === 0) return;
        setMoveDialogState({
            open: true,
            contractIds: [...selected],
            currentWorkspaceId: null,
            currentFolderId: null,
        });
    };

    const handleCloseMoveDialog = () => {
        setMoveDialogState((prev) => ({ ...prev, open: false }));
    };

    const handleAfterMove = () => {
        setSelected([]);
        // Refresh current view
        const tabIdentifier = TAB_IDENTIFIERS[activeTab];
        const offset = (currentPage - 1) * rowsPerPage;
        if (tabIdentifier === 'to_sign') {
            dispatch(fetchMyPendingSignatures(rowsPerPage, offset));
        } else if (sidebarFilterParamsRef.current) {
            dispatch(filterContracts(sidebarFilterParamsRef.current, rowsPerPage, offset));
        } else {
            dispatch(fetchContractsByTab(tabIdentifier, rowsPerPage, offset, true));
        }
    };

    // Open contract
    const handleOpenContract = async contractId => {
        dispatch(resetContract());
        navigate(`/contract/${contractId}`);
        enqueueSnackbar(t("contract_opened"), { variant: 'success' });

        // Google Analytics event for opening contract
        ReactGA.event({
            category: 'Contract',
            action: 'User opened a contract',
            label: `Contract ID: ${contractId}`
        });
        tracker.event({
            category: 'Contract',
            action: 'User opened a contract',
            label: `Contract ID: ${contractId}`
        });
    };

    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        dispatch(setCurrentTab(newValue));

        // Sync URL param
        const tabId = TAB_IDENTIFIERS[newValue];
        if (tabId && tabId !== 'all') {
            setSearchParams({ tab: tabId }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }

        // Reset to page 1 when changing tabs
        if (tabPagination[newValue]?.page !== 1) {
            dispatch(setTabPage(newValue, 1));
        }
    };

    const getFilteredAndSortedContracts = () => {
        // "Documents à signer" tab uses data from signature reducer
        if (TAB_IDENTIFIERS[activeTab] === 'to_sign') {
            return myPendingContracts;
        }

        // If any sidebar context or filter is active, use backend-filtered results
        if (activeAdvancedFilters > 0 || activeWorkspace || activeFolderRedux || activeSmartViewRedux || activePartyId || activeFilter !== 'none') {
            return filteredContracts || [];
        }

        // Default: use contracts from the current tab (backend-loaded)
        return contracts || [];
    };


    const handleUpdateContract_fn = async (contractId, contractData) => {
        // Dispatch the action and handle UI feedback
        try {
            dispatch(handleUpdateContract(contractId, contractData));

            // Update the selected contract immediately in local state too
            if (selectedContract && selectedContract.id === contractId) {
                setSelectedContract(prev => ({ ...prev, ...contractData }));
            }

            // Show success message
            enqueueSnackbar(t("contract_updated_success") || "Contract updated successfully", {
                variant: 'success'
            });

            // Google Analytics event for contract update
            ReactGA.event({
                category: 'Contract',
                action: 'User updated contract',
                label: `Contract ID: ${contractId}`
            });
            tracker.event({
                category: 'Contract',
                action: 'User updated contract',
                label: `Contract ID: ${contractId}`
            });
        } catch (error) {
            enqueueSnackbar(t("contract_update_error") || "Failed to update contract", {
                variant: 'error'
            });
        }
    }

    // Handle sidebar filter changes (workspace, folder, smart view, party)
    const handleSidebarFilter = (filterParams) => {
        // Store for composing with search/filter bar
        sidebarFilterParamsRef.current = filterParams;

        if (!filterParams) {
            // Clear all sidebar filters — clear filtered state and refetch current tab
            setActivePartyId(null);
            dispatch({ type: 'FILTER_CONTRACTS_SUCCESS', payload: [] });
            const tabIdentifier = TAB_IDENTIFIERS[activeTab];
            const page = tabPagination[activeTab]?.page || 1;
            const offset = (page - 1) * rowsPerPage;
            dispatch(fetchContractsByTab(tabIdentifier, rowsPerPage, offset, true));
            return;
        }

        // If it's a smart view filter
        if (filterParams.smartView) {
            setActivePartyId(null);
            dispatch(filterContracts({ smartView: filterParams.smartView }, rowsPerPage, 0));
            return;
        }

        // If it has workspace_id or folder_id, filter contracts
        if (filterParams.workspace_id || filterParams.folder_id) {
            setActivePartyId(null);
            dispatch(filterContracts(filterParams, rowsPerPage, 0));
            return;
        }

        // If it has party_ids, filter contracts
        if (filterParams.party_ids) {
            setActivePartyId(filterParams.party_ids[0] || null);
            dispatch(filterContracts(filterParams, rowsPerPage, 0));
        }
    };


    // Breadcrumb helpers
    const activeWorkspaceName = activeWorkspace
        ? workspaces.find(w => w.id === activeWorkspace)?.name
        : null;

    const findFolderName = (foldersArr, folderId) => {
        for (const f of foldersArr) {
            if (f.id === folderId) return f.name;
            if (f.children) {
                const found = findFolderName(f.children, folderId);
                if (found) return found;
            }
        }
        return null;
    };

    const activeFolderName = activeFolderRedux
        ? findFolderName(folders, activeFolderRedux)
        : null;

    const handleBreadcrumbAllContracts = () => {
        dispatch(setActiveWorkspace(null));
        dispatch(setActiveFolder(null));
        handleSidebarFilter(null);
    };

    const handleBreadcrumbWorkspace = () => {
        dispatch(setActiveFolder(null));
        if (activeWorkspace) {
            handleSidebarFilter({ workspace_id: activeWorkspace });
        }
    };

    // Kanban reorder handler — persists positions (and optionally status changes) to backend
    const handleKanbanReorder = async (updates, previousContracts) => {
        try {
            await dispatch(reorderKanbanContracts(updates, previousContracts));
        } catch (error) {
            enqueueSnackbar(t('contract_update_error', 'Failed to update contract'), { variant: 'error' });
        }
    };

    // Get contracts for the current view
    const viewContracts = getFilteredAndSortedContracts();

    return (
        <Box sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
        }}>
            {/* Left Sidebar */}
            {sidebarOpen && (
                <RepositorySidebar
                    onFilterChange={handleSidebarFilter}
                    onClose={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                minWidth: 0,
            }}>
                <Box sx={{
                    pb: { xs: 2, sm: 3 },
                    px: { xs: 2, sm: 2 },
                    width: '100%',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!sidebarOpen && (
                            <Tooltip title={t('repository.sidebar.open', 'Open sidebar')}>
                                <IconButton
                                    size="small"
                                    onClick={() => setSidebarOpen(true)}
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        mt: 1,
                                    }}
                                >
                                    <MenuOpenIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Box sx={{ flex: 1 }}>
                            <DashboardHeader
                                title={t("contract_dashboard")}
                                onVideoClick={() => setContractVideoModalOpen(true)}
                            />
                        </Box>
                    </Box>


                    {/* <Box sx={{ mb: 3 }}>
                        <ChartsOverview
                            statusData={statusData}
                            monthlyData={monthlyData}
                        />
                    </Box> */}

                    <SearchFilterBar
                        searchTerm={searchTerm}
                        handleSearch={handleSearch}
                        activeView={activeView}
                        setActiveView={setActiveView}
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        currentUser={user}
                        onAdvancedFilter={handleAdvancedFilter}
                        viewContext={activeMonitoringView || 'contracts'}
                        sidebarFilterParams={sidebarFilterParamsRef.current}
                    />

                    {/* Breadcrumb Navigation */}
                    <Box sx={{ mt: 1.5, mb: 1.5, px: 0.5 }}>
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />}
                            sx={{ '& .MuiBreadcrumbs-li': { fontSize: '0.85rem' } }}
                        >
                            <Link
                                component="button"
                                underline={(activeWorkspace || activeMonitoringView) ? 'hover' : 'none'}
                                onClick={handleBreadcrumbAllContracts}
                                sx={{
                                    color: (activeWorkspace || activeMonitoringView)
                                        ? theme.palette.text.secondary
                                        : theme.palette.text.primary,
                                    fontWeight: (activeWorkspace || activeMonitoringView) ? 400 : 600,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                    },
                                }}
                            >
                                {t('breadcrumb.allContracts')}
                            </Link>
                            {activeMonitoringView && (
                                <Typography
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {activeMonitoringView === 'regulatory_alerts'
                                        ? t('repository.sidebar.regulatoryAlerts', 'Regulatory Alerts')
                                        : t('repository.sidebar.contractLifecycle', 'Contract Life Cycle')}
                                </Typography>
                            )}
                            {!activeMonitoringView && activeWorkspaceName && (
                                <Link
                                    component="button"
                                    underline={activeFolderRedux ? 'hover' : 'none'}
                                    onClick={handleBreadcrumbWorkspace}
                                    sx={{
                                        color: activeFolderRedux
                                            ? theme.palette.text.secondary
                                            : theme.palette.text.primary,
                                        fontWeight: activeFolderRedux ? 400 : 600,
                                        fontSize: '0.85rem',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    {activeWorkspaceName}
                                </Link>
                            )}
                            {!activeMonitoringView && activeFolderName && (
                                <Typography
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {activeFolderName}
                                </Typography>
                            )}
                        </Breadcrumbs>
                    </Box>

                    {/* Monitoring Views */}
                    {activeMonitoringView === 'regulatory_alerts' && (
                        <Box sx={{ mt: 1 }}>
                            <RegulatoryAlertsList searchTerm={searchTerm} />
                        </Box>
                    )}
                    {activeMonitoringView === 'contract_lifecycle' && (
                        <Box sx={{ mt: 1 }}>
                            <LifecycleList searchTerm={searchTerm} />
                        </Box>
                    )}

                    {/* Contract List (hidden when monitoring view is active) */}
                    {!activeMonitoringView && <Box sx={{ mt: 1 }}>
                            {(TAB_IDENTIFIERS[activeTab] === 'to_sign' ? myPendingLoading : (loading || loadingFilter)) ? (
                                <LoadingState title={t("loading_contracts")} />
                            ) : viewContracts.length === 0 ? (
                                <EmptyState
                                    activeTab={activeTab}
                                    searchTerm={searchTerm}
                                    isSearching={searchTerm.trim() !== ''}
                                />
                            ) : (
                                <>
                                    {activeView === 'kanban' ? (
                                        <ContractKanban
                                            contracts={viewContracts}
                                            onContractClick={handleContractClick}
                                            onReorder={handleKanbanReorder}
                                        />
                                    ) : activeView === 'grid' ? (
                                        <ContractGrid
                                            filteredContracts={viewContracts}
                                            handleContractClick={handleContractClick}
                                            handleShareContract={id => handleShareContract(id)}
                                            handleDownloadContract={id => handleDownloadContract(id)}
                                            handleOpenDeleteDialog={ids => handleOpenDeleteDialog(ids)}
                                            handleOpenContract={handleOpenContract}
                                            handleMoveContract={handleMoveContract}
                                        />
                                    ) : activeView === 'table' ? (
                                        <ContractTable
                                            filteredContracts={viewContracts}
                                            onContractClick={handleContractClick}
                                            onContractDoubleClick={handleOpenContract}
                                            selectedContractId={selectedContract?.id}
                                            selectedIds={selected}
                                            onSelectionChange={setSelected}
                                            handleMoveContract={handleMoveContract}
                                        />
                                    ) : (
                                        <ContractList
                                            filteredContracts={viewContracts}
                                            handleContractClick={handleContractClick}
                                            handleShareContract={id => handleShareContract(id)}
                                            handleDownloadContract={id => handleDownloadContract(id)}
                                            handleOpenDeleteDialog={ids => handleOpenDeleteDialog(ids)}
                                            handleOpenContract={handleOpenContract}
                                            handleMoveContract={handleMoveContract}
                                        />
                                    )}

                                    {/* Pagination — hidden for Kanban view (all items loaded) */}
                                    {activeView !== 'kanban' && (() => {
                                        const filteredCount = viewContracts.length;
                                        const hasSearchOrFilters = searchTerm.trim() !== '' || activeFilter !== 'none' || activeAdvancedFilters > 0;

                                        const isToSignTab = TAB_IDENTIFIERS[activeTab] === 'to_sign';
                                        const totalItems = hasSearchOrFilters ? filteredCount : (isToSignTab ? myPendingCount : currentTotal);

                                        const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
                                        const pageNumber = Math.min(currentPage, totalPages);

                                        return totalPages > 1 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                                <Pagination
                                                    count={Math.floor(totalPages)}
                                                    page={pageNumber}
                                                    onChange={handlePageChange}
                                                    color="primary"
                                                    size="large"
                                                    siblingCount={1}
                                                    showFirstButton
                                                    showLastButton
                                                />
                                            </Box>
                                        ) : null;
                                    })()}
                                </>
                            )}
                    </Box>}
                </Box>
            </Box>

            {/* Dialogs */}
            <ContractDetailDialog
                open={isDetailModalOpen}
                onOpen={() => {
                    setIsDetailModalOpen(false);
                    handleOpenContract(selectedContract?.id)
                }}
                onClose={handleCloseDetailDialog}
                contract={selectedContract}
                onDownload={() => {
                    setIsDetailModalOpen(false);
                    handleDownloadContract(selectedContract?.id, 'detail');
                }}
                onShare={() => {
                    setIsDetailModalOpen(false);
                    handleShareContract(selectedContract?.id, 'detail');
                }}
                onDelete={() => {
                    setIsDetailModalOpen(false);
                    handleOpenDeleteDialog([selectedContract?.id], 'detail');
                }}
                onUpdateContract={handleUpdateContract_fn}
            />

            <DownloadDialog
                open={isDownloadDialogOpen}
                onClose={handleCloseDownloadDialog}
                contractId={currentContractId}
            />

            <ShareContractModal
                open={isShareModalOpen}
                onClose={handleCloseShareDialog}
                onShare={handleShare}
                organizationName={user?.organization_name}
            />

            <DeleteConfirmationDialog
                open={isDeleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleDeleteContracts}
                selectedCount={selected.length}
                isDeleting={loadingDelete}
            />

            {/* Contract Tutorial Video Modal */}
            <TutorialModal
                open={contractVideoModalOpen}
                onClose={() => setContractVideoModalOpen(false)}
                defaultTab="contract"
            />

            <MoveContractDialog
                open={moveDialogState.open}
                onClose={handleCloseMoveDialog}
                contractIds={moveDialogState.contractIds}
                currentWorkspaceId={moveDialogState.currentWorkspaceId}
                currentFolderId={moveDialogState.currentFolderId}
                onMoved={handleAfterMove}
            />

            {/* Bulk action bar — visible when contracts are selected (table view) */}
            {selected.length > 0 && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderRadius: '999px',
                        bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#1a1a1a',
                        color: '#fff',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mr: 1 }}>
                        {t('bulk.selected', '{{count}} selected', { count: selected.length })}
                    </Typography>
                    <Box
                        component="button"
                        onClick={handleBulkMove}
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: '#fff',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        {t('moveContract.menuItem', 'Move to…')}
                    </Box>
                    <Box
                        component="button"
                        onClick={() => handleOpenDeleteDialog(selected)}
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: theme.palette.error.light,
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        {t('delete', 'Delete')}
                    </Box>
                    <Box
                        component="button"
                        onClick={() => setSelected([])}
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        {t('common.cancel', 'Clear')}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ContractHistory;